/**
 * Guardian surface — pages beyond the ward-home flow.
 *
 * Grouped in one file to keep the surface's bundle tight; each is small.
 * Every page accepts (:student = admission_number) and re-uses server auth
 * via requireWardForCaller so we never leak another guardian's ward.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Button, Card, DatePicker, Empty, Form, Input, Modal, Radio, Skeleton, Space, Table, Tabs, Tag, Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type {
  GuardianAbsenceRequest, GuardianDocumentRow, GuardianExamRow,
  GuardianInvoiceRow, GuardianTimetableSlot,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';

const DAY_LABEL: Record<number, string> = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun',
};

function BackLink({ to }: { to: string }) {
  return <Link to={to}><Button icon={<ArrowLeftOutlined />} type="text" size="small">Back</Button></Link>;
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/* ------------------------- Ward timetable ---------------------------- */

export function GuardianTimetablePage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const q = useQuery({
    queryKey: ['guardian-timetable', institution, student],
    queryFn: () => productApi.getGuardianTimetable(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const grid = new Map<number, Map<number, GuardianTimetableSlot>>();
  let maxPeriod = 0;
  for (const s of q.data.slots) {
    if (!grid.has(s.day_of_week)) grid.set(s.day_of_week, new Map());
    grid.get(s.day_of_week)!.set(s.period, s);
    if (s.period > maxPeriod) maxPeriod = s.period;
  }
  const days = [1, 2, 3, 4, 5, 6];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>
        {q.data.ward.name}'s timetable
      </Typography.Title>
      {maxPeriod === 0 ? (
        <Empty description="No timetable set for this section yet." />
      ) : (
        <Card size="small" bodyStyle={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={cellHead}>Period</th>
                {days.map((d) => (<th key={d} style={cellHead}>{DAY_LABEL[d]}</th>))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxPeriod }, (_, i) => i + 1).map((p) => (
                <tr key={p}>
                  <td style={{ ...cellBody, fontWeight: 600, background: '#fafbfc' }}>P{p}</td>
                  {days.map((d) => {
                    const s = grid.get(d)?.get(p);
                    if (!s) return <td key={d} style={cellBody} />;
                    return (
                      <td key={d} style={{ ...cellBody, background: '#eaf3fb' }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{s.subject_name}</div>
                        {s.teacher_name && <div style={{ fontSize: 11, color: '#5b6478' }}>{s.teacher_name}</div>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </Space>
  );
}

/* ------------------------- Results index + detail ------------------- */

export function GuardianResultsIndexPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['guardian-results', institution, student],
    queryFn: () => productApi.getGuardianResults(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>
        {q.data.ward.name}'s results
      </Typography.Title>
      {q.data.exams.length === 0 ? (
        <Empty description="No exams scheduled for this section yet." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {q.data.exams.map((e) => (
            <ExamCard
              key={e.exam_id}
              exam={e}
              onOpen={() => navigate(`/i/${institution}/parent/${student}/results/${e.exam_id}`)}
            />
          ))}
        </div>
      )}
    </Space>
  );
}

function ExamCard({ exam, onOpen }: { exam: GuardianExamRow; onOpen: () => void }) {
  const marked = exam.subjects_marked === exam.subjects_total;
  return (
    <Card size="small" hoverable onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <b>{exam.exam_name}</b>
        <Tag>{exam.session_label}</Tag>
        <div style={{ flex: 1 }} />
        <Tag color={exam.status === 'published' ? 'success' : 'default'}>{exam.status}</Tag>
      </div>
      <div style={{ fontSize: 12, color: '#5b6478', marginTop: 4 }}>
        {exam.date_from && exam.date_to && `${exam.date_from} → ${exam.date_to} · `}
        {marked
          ? <b>{exam.obtained}/{exam.max} · {exam.percentage}%</b>
          : `${exam.subjects_marked}/${exam.subjects_total} subjects marked`}
      </div>
    </Card>
  );
}

export function GuardianExamDetailPage() {
  const { institution = '', student = '', examId = '' } = useParams<{ institution: string; student: string; examId: string }>();
  const q = useQuery({
    queryKey: ['guardian-exam', institution, student, examId],
    queryFn: () => productApi.getGuardianExamDetail(institution, student, examId),
    enabled: !!institution && !!student && !!examId,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const d = q.data;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}/results`} />
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>{d.exam.name}</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>{d.exam.session_label}</Typography.Text>
      </div>
      <Card size="small">
        <Typography.Paragraph style={{ margin: 0, fontSize: 15 }}>{d.headline}</Typography.Paragraph>
        {d.totals.percentage !== null && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#5b6478' }}>
            {d.totals.obtained} / {d.totals.max} across {d.subjects.length} subjects
          </div>
        )}
      </Card>
      <Card size="small" title="Subjects">
        <Table
          size="small"
          pagination={false}
          rowKey={(r) => r.subject_name}
          dataSource={d.subjects}
          columns={[
            { title: 'Subject', dataIndex: 'subject_name', key: 's' },
            {
              title: 'Marks', key: 'm', align: 'right',
              render: (_, r) => r.absent ? '—' : r.obtained === null ? '—' : `${r.obtained} / ${r.max_marks}`,
            },
            {
              title: 'Result', dataIndex: 'result', key: 'res',
              render: (v: string) => <Tag color={v === 'pass' ? 'success' : v === 'fail' ? 'error' : v === 'absent' ? 'warning' : 'default'}>{v}</Tag>,
            },
          ]}
        />
      </Card>
    </Space>
  );
}

/* ------------------------- Documents ---------------------------------- */

const DOC_TONE: Record<GuardianDocumentRow['kind'], string> = {
  receipt: 'blue', transfer_certificate: 'purple', bonafide: 'green', character: 'geekblue', custom: 'default',
};
const DOC_LABEL: Record<GuardianDocumentRow['kind'], string> = {
  receipt: 'receipt', transfer_certificate: 'TC', bonafide: 'bonafide', character: 'character', custom: 'custom',
};

export function GuardianDocumentsPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const q = useQuery({
    queryKey: ['guardian-documents', institution, student],
    queryFn: () => productApi.getGuardianDocuments(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>
        {q.data.ward.name}'s documents
      </Typography.Title>
      {q.data.documents.length === 0 ? (
        <Empty description="No receipts or certificates yet." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {q.data.documents.map((d) => (
            <Card key={d.code + d.kind} size="small">
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <Tag color={DOC_TONE[d.kind]}>{DOC_LABEL[d.kind]}</Tag>
                <div style={{ flex: 1 }}>
                  <b style={{ fontSize: 14 }}>{d.title}</b>
                  <div style={{ fontSize: 11, color: '#5b6478' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{d.code}</span> · issued {d.issued_on}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Space>
  );
}

/* ------------------------- Sick-day / absence (H-01) ------------------ */

interface AbsenceForm { date: Dayjs; status: GuardianAbsenceRequest['status']; reason: string; }

export function GuardianAbsencePage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm<AbsenceForm>();
  const m = useMutation({
    mutationFn: (input: GuardianAbsenceRequest) => productApi.reportGuardianAbsence(institution, student, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guardian-attendance', institution, student] });
      void message.success('Reported to the teacher');
      navigate(`/i/${institution}/parent/${student}`);
    },
  });
  const body = m.error instanceof ApiError ? (m.error.body as { message?: string; error?: string } | null) : null;
  const err = body?.message ?? body?.error ?? (m.error instanceof Error ? m.error.message : null);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>Report an absence</Typography.Title>
      <Alert
        type="info" showIcon
        message="The teacher will see your note before marking the register."
        description="Once the teacher has already submitted, use messages instead."
      />
      <Card size="small">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 'sick' as GuardianAbsenceRequest['status'] }}
          onFinish={(v) => m.mutate({
            date: v.date.format('YYYY-MM-DD'),
            status: v.status,
            reason: v.reason.trim(),
          })}
        >
          <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Pick a date' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Reason type" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="sick">Sick</Radio.Button>
              <Radio.Button value="absent">Absent</Radio.Button>
              <Radio.Button value="leave">Family reason</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="reason" label="Note for the teacher" rules={[{ required: true, min: 3, max: 500 }]}>
            <Input.TextArea rows={3} placeholder="A short note — e.g. flu, doctor visit, family function" />
          </Form.Item>
          {err && <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} />}
          <Button type="primary" htmlType="submit" loading={m.isPending} block>Send</Button>
        </Form>
      </Card>
    </Space>
  );
}

/* ------------------------- Fees history ------------------------------- */

export function GuardianFeesHistoryPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const q = useQuery({
    queryKey: ['guardian-fees-history', institution, student],
    queryFn: () => productApi.getGuardianFeesHistory(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const currentTab = q.data.current_session_id ?? q.data.sessions[0]?.session_id ?? 'none';
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>Fees history</Typography.Title>
      {q.data.sessions.length === 0 ? (
        <Empty description="No invoices raised for this ward yet." />
      ) : (
        <Tabs
          defaultActiveKey={currentTab}
          items={q.data.sessions.map((s) => ({
            key: s.session_id,
            label: (
              <span>
                {s.label} {s.session_id === q.data.current_session_id && <Tag color="success">current</Tag>}
              </span>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Card size="small">
                  <Space size={24}>
                    <Stat title="Billed" value={`₹${paise(s.totals.billed)}`} />
                    <Stat title="Paid" value={`₹${paise(s.totals.paid)}`} tone="ok" />
                    <Stat title="Open" value={`₹${paise(s.totals.open)}`} tone={s.totals.open > 0 ? 'warn' : 'ok'} />
                  </Space>
                </Card>
                <Card size="small" title={`Invoices (${s.invoices.length})`}>
                  <Table<GuardianInvoiceRow>
                    size="small" pagination={false} rowKey="invoice_id"
                    dataSource={s.invoices}
                    columns={[
                      { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
                      { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
                      { title: 'Paid', dataIndex: 'paid_paise', key: 'p', align: 'right', render: (v: number) => `₹${paise(v)}` },
                      { title: 'Due', dataIndex: 'due_on', key: 'd' },
                      { title: 'Status', dataIndex: 'status', key: 's', width: 90,
                        render: (v: string, r) => v === 'paid' ? <Tag color="success">paid</Tag> : (r.days_overdue > 0 ? <Tag color="error">{r.days_overdue}d late</Tag> : <Tag>due</Tag>),
                      },
                    ]}
                  />
                </Card>
                {s.receipts.length > 0 && (
                  <Card size="small" title={`Receipts (${s.receipts.length})`}>
                    <Table
                      size="small" pagination={false} rowKey="receipt_number"
                      dataSource={s.receipts}
                      columns={[
                        { title: 'Receipt', dataIndex: 'receipt_number', key: 'n', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                        { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
                        { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
                        { title: 'Method', dataIndex: 'method', key: 'm', render: (v: string) => <Tag>{v}</Tag> },
                        { title: 'Date', dataIndex: 'received_on', key: 'd' },
                      ]}
                    />
                  </Card>
                )}
              </Space>
            ),
          }))}
        />
      )}
    </Space>
  );
}

function Stat({ title, value, tone }: { title: string; value: string; tone?: 'ok' | 'warn' }) {
  const color = tone === 'ok' ? '#1c6442' : tone === 'warn' ? '#a24040' : '#14213d';
  return (
    <div>
      <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

/* ------------------------- Year view ---------------------------------- */

export function GuardianYearPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const q = useQuery({
    queryKey: ['guardian-year', institution, student],
    queryFn: () => productApi.getGuardianYear(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const y = q.data;
  const events = useMemo(() => {
    const list: Array<{ date: string; kind: string; label: string; tone: string }> = [];
    for (const t of y.terms) {
      list.push({ date: t.start_on, kind: 'term', label: `${t.label} begins`, tone: 'blue' });
      list.push({ date: t.end_on, kind: 'term', label: `${t.label} ends`, tone: 'blue' });
    }
    for (const h of y.holidays) list.push({ date: h.date, kind: 'holiday', label: h.label, tone: 'green' });
    for (const e of y.exams) {
      if (e.date_from) list.push({ date: e.date_from, kind: 'exam', label: `${e.name}`, tone: 'purple' });
    }
    return list.sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [y]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {y.ward.name}'s year
        </Typography.Title>
        {y.session && (
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {y.session.label} · {y.session.start_on} → {y.session.end_on}
          </Typography.Text>
        )}
      </div>
      {!y.session ? (
        <Empty description="No current session set." />
      ) : events.length === 0 ? (
        <Empty description="No terms, holidays or exams scheduled yet." />
      ) : (
        <Card size="small">
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            <div style={{ position: 'absolute', left: 8, top: 4, bottom: 4, width: 2, background: '#eef0f3' }} />
            {events.map((e, i) => (
              <div key={i} style={{ position: 'relative', paddingBottom: 14 }}>
                <div style={{
                  position: 'absolute', left: -20, top: 4,
                  width: 12, height: 12, borderRadius: 6,
                  background: '#fff', border: `2px solid ${dotColor(e.tone)}`,
                }} />
                <div style={{ fontSize: 12, color: '#5b6478' }}>{e.date}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                  <Tag color={e.tone}>{e.kind}</Tag>
                  <b>{e.label}</b>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Space>
  );
}

function dotColor(tone: string): string {
  return tone === 'blue' ? '#2f6ab0' : tone === 'green' ? '#1c6442' : tone === 'purple' ? '#8a5cf6' : '#8a94a6';
}

/* ------------------------- Profile ------------------------------------ */

export function GuardianProfilePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const home = useQuery({
    queryKey: ['guardian-home', institution],
    queryFn: () => productApi.getGuardianHome(institution),
    enabled: !!institution,
  });
  if (home.isLoading || !home.data) return <Skeleton active paragraph={{ rows: 4 }} />;
  const g = home.data.guardian;
  if (!g) return <Alert type="warning" showIcon message="No guardian record linked to your email yet." />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent`} />
      <Typography.Title level={3} style={{ margin: 0 }}>Profile</Typography.Title>
      <Card size="small">
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, fontSize: 13 }}>
          <div style={{ color: '#8a94a6' }}>Name</div><div>{g.name}</div>
          <div style={{ color: '#8a94a6' }}>Phone</div><div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{g.phone}</div>
          <div style={{ color: '#8a94a6' }}>Email</div><div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{g.email ?? '—'}</div>
        </div>
      </Card>
      <Card size="small" title={`Wards (${home.data.wards.length})`}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {home.data.wards.map((w) => (
            <div key={w.student_id} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <b>{w.name}</b>
              <Tag>{w.grade}{w.section ? `-${w.section}` : ''}</Tag>
              <span style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{w.admission_number}</span>
              {w.is_primary && <Tag color="success">primary</Tag>}
              {w.status !== 'enrolled' && <Tag>{w.status}</Tag>}
            </div>
          ))}
        </Space>
      </Card>
      <Alert
        type="info" showIcon
        message="Contact and notification preferences edit form ships with the paywall + Comms module."
      />
    </Space>
  );
}

const cellHead: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 12,
  background: '#f7f7f5', borderBottom: '1px solid #eef0f3', color: '#5b6478',
};
const cellBody: React.CSSProperties = {
  padding: '10px', borderBottom: '1px solid #f1f1f4', verticalAlign: 'top', minWidth: 90,
};

/* ------------------------- Placeholder pages -------------------------- */

/** Bus tracking — reads /guardian/wards/:adm/transport. */
export function GuardianTransportPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const q = useQuery({
    queryKey: ['guardian-transport', institution, student],
    queryFn: () => productApi.getGuardianTransport(institution, student),
    enabled: !!institution && !!student,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 4 }} />;
  const a = q.data.assignment;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>
        {q.data.ward.name}'s bus
      </Typography.Title>
      {!a ? (
        <Alert type="info" showIcon
          message="Not on a school bus route"
          description="If this looks wrong, ask the school office — they can assign a route from the admin transport page." />
      ) : (
        <>
          <Card size="small">
            <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Tag color="blue" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{a.route_code}</Tag>
              <Typography.Title level={4} style={{ margin: 0 }}>{a.route_name}</Typography.Title>
              <div style={{ flex: 1 }} />
              <Tag>{a.boarding_pattern.replace(/_/g, ' ')}</Tag>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 12, fontSize: 13 }}>
              <div>
                <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Stop</div>
                <div style={{ fontWeight: 600 }}>{a.stop_name}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Vehicle</div>
                <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                  {a.vehicle_registration ?? '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Driver</div>
                <div>{a.driver_name ?? '—'}
                  {a.driver_phone && <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, marginLeft: 8, color: '#5b6478' }}>{a.driver_phone}</span>}
                </div>
              </div>
            </div>
          </Card>
          <Alert type="info" showIcon
            message="Live position + 1-km-out alert coming later"
            description="This page shows the assigned route today. Live map + geofence alerts ship with the vehicle-tracking integration." />
        </>
      )}
    </Space>
  );
}

/* ------------------------- Consent one-tap (P-05) --------------------- */

export function GuardianConsentsPage() {
  const { institution = '', student = '' } = useParams<{ institution: string; student: string }>();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['guardian-consents', institution, student],
    queryFn: () => productApi.getGuardianConsents(institution, student),
    enabled: !!institution && !!student,
  });
  const submit = useMutation({
    mutationFn: (input: { topic_id: string; choice: 'yes' | 'no' }) =>
      productApi.submitGuardianConsent(institution, student, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guardian-consents', institution, student] });
      void message.success('Saved');
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null)?.message ?? (e.body as { error?: string } | null)?.error ?? null : null;
      void message.error(msg ?? 'Failed to save');
    },
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 5 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BackLink to={`/i/${institution}/parent/${student}`} />
      <Typography.Title level={3} style={{ margin: 0 }}>{q.data.ward.name}'s consents</Typography.Title>
      <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
        Tap yes or no. You can change your answer any time before the school closes the topic.
      </Typography.Text>

      {q.data.rows.length === 0 ? (
        <Empty description="Nothing to consent to right now." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {q.data.rows.map((r) => (
            <Card key={r.topic_id} size="small">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <Tag style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.code}</Tag>
                <b style={{ fontSize: 15 }}>{r.title}</b>
                <div style={{ flex: 1 }} />
                {r.choice === 'yes' && <Tag color="success">yes</Tag>}
                {r.choice === 'no' && <Tag color="error">no</Tag>}
                {r.choice === null && <Tag>pending</Tag>}
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#3f4a63', whiteSpace: 'pre-wrap' }}>{r.description}</div>
              {r.at && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#8a94a6' }}>
                  You answered {r.choice} on {new Date(r.at).toLocaleString()}
                </div>
              )}
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <Button
                  type={r.choice === 'yes' ? 'primary' : 'default'}
                  size="large"
                  block
                  loading={submit.isPending}
                  onClick={() => submit.mutate({ topic_id: r.topic_id, choice: 'yes' })}
                >
                  Yes
                </Button>
                <Button
                  danger={r.choice === 'no'}
                  type={r.choice === 'no' ? 'primary' : 'default'}
                  size="large"
                  block
                  loading={submit.isPending}
                  onClick={() => submit.mutate({ topic_id: r.topic_id, choice: 'no' })}
                >
                  No
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Space>
  );
}

